import React, { useState } from 'react'
import styled from 'styled-components'
import SegmentService from 'redux/config/services/SegmentService'
import Chevron from 'core-system/Icons/Actions/Chevron'
import { WorkforceSyncStage } from './WorkforceSyncStage'
import { useDispatch, useSelector } from 'react-redux'
import { employerActions } from 'redux/employer/employerSlice'
import employerService from 'redux/employer/employerService'
import { extraOnboardingCopy } from '../OnboardingUtils'
import HeaderTextButton from 'core-system/ButtonV2/HeaderTextButton'
import { AppState } from 'redux/config/store'
import { useGateValue } from '@statsig/react-bindings'
import { onboardingActions } from '../onboardingActions'
import FinchConnect from 'features/Finch/components/FinchConnect'

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const SetupOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

interface OnboardingWorkforceSetupProps {
  setLoading: (loading: boolean) => void
  setWorkforceSyncStage: (step: WorkforceSyncStage) => void
}

const OnboardingWorkforceSetup = React.memo(
  (props: OnboardingWorkforceSetupProps) => {
    const { setLoading, setWorkforceSyncStage } = props
    const [showSelectProvidersModal, setShowSelectProvidersModal] =
      useState(false)
    const { email } = useSelector((state: AppState) => state.user.profile)
    const isConnectedAccountEnabled = useGateValue('connected_account')

    const dispatch = useDispatch()

    const createConnectedAccount = async (email: string) => {
      await employerService.createConnectedAccount(email).then((res) => {
        dispatch(employerActions.createConnectedAccountSuccess(res.data))
      })
    }

    const onSuccess = ({ code }) => {
      setLoading(true)
      employerService
        .integrateHris(code)
        .then((res) => {
          dispatch(employerActions.integrateHrisSuccess(res.data))
        })
        .then(() => {
          if (isConnectedAccountEnabled) {
            createConnectedAccount(email)
              .then(() => setLoading(false))
              .then(() => setWorkforceSyncStage('finchSync'))
          } else {
            setLoading(false)
            setWorkforceSyncStage('finchSync')
          }
          dispatch(onboardingActions.updateWorkforceSyncType('HRIS'))
        })
    }

    const handleSelectProvidersModal = React.useCallback(() => {
      SegmentService.track('hris-connect-modal-opened', {
        onboardingStep: 'workforceSetup',
      })
      setShowSelectProvidersModal(true)
    }, [])

    return (
      <Container>
        <SetupOptions>
          <HeaderTextButton
            header={extraOnboardingCopy.setupWorkforce.automated.header}
            text={extraOnboardingCopy.setupWorkforce.automated.text}
            iconRight={<Chevron style={{ transform: 'rotate(-90deg)' }} />}
            onClick={handleSelectProvidersModal}
          />
          <HeaderTextButton
            header={extraOnboardingCopy.setupWorkforce.manual.header}
            text={extraOnboardingCopy.setupWorkforce.manual.text}
            iconRight={<Chevron style={{ transform: 'rotate(-90deg)' }} />}
            onClick={() => setWorkforceSyncStage('csvUpload')}
          />
          <FinchConnect
            open={showSelectProvidersModal}
            onClose={() => setShowSelectProvidersModal(false)}
            onSuccess={onSuccess}
          />
        </SetupOptions>
      </Container>
    )
  }
)

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  OnboardingWorkforceSetup.displayName = 'OnboardingWorkforceSetup'
}

export default OnboardingWorkforceSetup
